<template>
	<div class="global-shifts-filter d-flex align-center">
		<v-btn-toggle
			v-model="periodType"
			mandatory
			dense
			class="global-shifts-filter__toggle"
			@change="onPeriodTypeChange"
		>
			<v-btn
				value="month"
				small
				depressed
				:color="periodType === 'month' ? 'primary' : ''"
				:class="{ 'global-shifts-filter__btn-active': periodType === 'month' }"
			>
				{{ $t('modules.customdata.global_shifts_filter.month') }}
			</v-btn>
			<v-btn
				value="week"
				small
				depressed
				:color="periodType === 'week' ? 'primary' : ''"
				:class="{ 'global-shifts-filter__btn-active': periodType === 'week' }"
			>
				{{ $t('modules.customdata.global_shifts_filter.week') }}
			</v-btn>
		</v-btn-toggle>

		<v-select
			v-model="periodValues"
			:items="periodItems"
			item-value="value"
			item-text="text"
			:label="$t('modules.customdata.global_shifts_filter.period')"
			outlined
			dense
			hide-details
			multiple
			chips
			small-chips
			class="global-shifts-filter__select ml-2"
			@change="onFilterChange"
		>
			<template v-slot:selection="{ item, index }">
				<v-chip v-if="index === 0" small>
					<span>{{ item?.text || item }}</span>
				</v-chip>
				<span
					v-if="index === 1"
					class="global-shifts-filter__others grey--text text-caption"
				>
					(+{{ periodValues.length - 1 }} {{ $t('modules.customdata.global_shifts_filter.others') }})
				</span>
			</template>
		</v-select>

		<v-select
			v-model="years"
			:items="yearItems"
			item-value="value"
			item-text="text"
			:label="$t('modules.customdata.global_shifts_filter.year')"
			outlined
			dense
			hide-details
			multiple
			chips
			small-chips
			class="global-shifts-filter__select ml-2"
			@change="onFilterChange"
		>
			<template v-slot:selection="{ item, index }">
				<v-chip v-if="index === 0" small>
					<span>{{ item?.text || item }}</span>
				</v-chip>
				<span
					v-if="index === 1"
					class="global-shifts-filter__others grey--text text-caption"
				>
					(+{{ years.length - 1 }} {{ $t('modules.customdata.global_shifts_filter.others') }})
				</span>
			</template>
		</v-select>

		<v-select
			v-model="activityIds"
			:items="activityItems"
			:label="$t('modules.customdata.global_shifts_filter.all_activities')"
			item-text="label"
			item-value="id"
			outlined
			dense
			hide-details
			multiple
			chips
			small-chips
			class="global-shifts-filter__select ml-2"
			@change="onFilterChange"
		>
			<template v-slot:selection="{ item, index }">
				<v-chip v-if="index === 0" small>
					<span>{{ item?.label || item }}</span>
				</v-chip>
				<span
					v-if="index === 1"
					class="global-shifts-filter__others grey--text text-caption"
				>
					(+{{ activityIds.length - 1 }} {{ $t('modules.customdata.global_shifts_filter.others') }})
				</span>
			</template>
			<template v-slot:prepend-item>
				<v-list-item
					ripple
					@mousedown.prevent
					@click="toggleSelectAllActivities"
				>
					<v-list-item-action>
						<v-icon :color="isAllActivitiesSelected ? 'primary' : ''">
							{{ isAllActivitiesSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
						</v-icon>
					</v-list-item-action>
					<v-list-item-content>
						<v-list-item-title>{{ $t('modules.customdata.global_shifts_filter.select_all') }}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-divider class="mt-2"></v-divider>
			</template>
		</v-select>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

const MONTH_OPTIONS = [
	{ value: 'all', textKey: 'all_months' },
	{ value: 'current', textKey: 'this_month' },
	...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, textKey: `months.${i + 1}` })),
];

const WEEK_OPTIONS = [
	{ value: 'all', textKey: 'all_weeks' },
	{ value: 'current', textKey: 'this_week' },
	...Array.from({ length: 52 }, (_, i) => ({ value: i + 1, textKey: null })),
];

export default {
	name: 'GlobalShiftsFilter',

	props: {
		activityColumn: {
			type: String,
			default: null,
		},
	},

	computed: {
		...mapGetters(['custom_data_global_shifts_filter', 'custom_data_global_shifts_available_years', 'custom_data_linked_tables_values', 'bot_settings']),

		periodType: {
			get() {
				return this.custom_data_global_shifts_filter?.periodType || 'month';
			},
			set(val) {
				this.updateFilter({ periodType: val });
			},
		},

		periodValues: {
			get() {
				return this.custom_data_global_shifts_filter?.periodValues || [];
			},
			set(val) {
				this.updateFilter({ periodValues: val });
			},
		},

		years: {
			get() {
				return this.custom_data_global_shifts_filter?.years || [];
			},
			set(val) {
				this.updateFilter({ years: val });
			},
		},

		activityIds: {
			get() {
				return this.custom_data_global_shifts_filter?.activityIds || [];
			},
			set(val) {
				this.updateFilter({ activityIds: val });
			},
		},

		periodItems() {
			const t = (key) => this.$t(`modules.customdata.global_shifts_filter.${key}`);
			if (this.periodType === 'month') {
				return MONTH_OPTIONS.map((opt) => ({
					value: opt.value,
					text: opt.textKey ? t(opt.textKey) : String(opt.value),
				}));
			}
			return WEEK_OPTIONS.map((opt) => ({
				value: opt.value,
				text: opt.textKey ? t(opt.textKey) : String(opt.value),
			}));
		},

		yearItems() {
			const availableYears = this.custom_data_global_shifts_available_years || [];
			const years = availableYears.length > 0
				? [...availableYears].sort((a, b) => b - a).map((y) => ({ value: y, text: String(y) }))
				: this.getFallbackYears();
			return [
				{ value: 'all', text: this.$t('modules.customdata.global_shifts_filter.all_years') },
				{ value: 'current', text: this.$t('modules.customdata.global_shifts_filter.this_year') },
				...years,
			];
		},

		activities() {
			if (!this.activityColumn) return [];
			return this.custom_data_linked_tables_values?.[this.activityColumn] || [];
		},

		activityItems() {
			return this.activities.map((a) => ({
				id: a.id ?? a.is_id,
				label: a.label || a.name || String(a.id),
			}));
		},

		isAllActivitiesSelected() {
			if (!this.activities.length) return false;
			return this.activityIds.length >= this.activities.length;
		},
	},

	mounted() {
		this.$store.dispatch('customDataLoadGlobalShiftsFilter');
		this.$store.dispatch('customDataFetchReportYears');
	},

	methods: {
		...mapActions(['customDataSetGlobalShiftsFilter']),

		updateFilter(payload) {
			this.customDataSetGlobalShiftsFilter(payload);
		},

		onPeriodTypeChange() {
			this.updateFilter({ periodValues: [] });
			this.onFilterChange();
		},

		onFilterChange() {
			this.$emit('change');
		},

		toggleSelectAllActivities() {
			if (this.isAllActivitiesSelected) {
				this.activityIds = [];
			} else {
				this.activityIds = this.activityItems.map((a) => a.id);
			}
			this.onFilterChange();
		},

		getFallbackYears() {
			const currentYear = new Date().getFullYear();
			const years = [];
			for (let y = currentYear; y >= currentYear - 10; y--) {
				years.push({ value: y, text: String(y) });
			}
			return years;
		},
	},
};
</script>

<style lang="scss" scoped>
.global-shifts-filter {
	overflow-x: auto;
	padding: 10px 0;

	&__others {
		flex-shrink: 0;
	}

	&__toggle {
		border: 1px solid rgba(0, 0, 0, 0.38);
		border-radius: 4px;

		.v-btn {
			border: none !important;
			box-shadow: none !important;
		}

		.global-shifts-filter__btn-active {
			color: white !important;

			&::before {
				background-color: transparent !important;
			}
		}
	}

	&__select {
		max-width: 200px;
		min-width: 140px;
		font-size: 14px;

		::v-deep .v-input {
			min-height: 0 !important;
			font-size: 14px;
		}

		::v-deep .v-input__control {
			min-height: 0 !important;
			height: 31px !important;
		}

		::v-deep .v-input__slot {
			min-height: 28px !important;
			padding-top: 0 !important;
			padding-bottom: 0 !important;
			align-items: center;
		}

		::v-deep .v-text-field--outlined fieldset {
			padding-top: 2px !important;
			padding-bottom: 2px !important;
			padding-left: 12px !important;
			padding-right: 12px !important;
		}

		::v-deep .v-text-field--outlined .v-input__control {
			padding-top: 0 !important;
			padding-bottom: 0 !important;
		}

		::v-deep .v-select__selection,
		::v-deep .v-label,
		::v-deep input {
			font-size: 14px !important;
		}

		::v-deep .v-select__slot .v-select__selections {
			min-height: 24px;
			padding-top: 2px;
			padding-bottom: 2px;
			flex-wrap: nowrap !important;
			overflow: hidden;
			align-items: center;
		}

		::v-deep .v-select__selections .v-chip {
			flex-shrink: 1;
			max-width: 120px;
			font-size: 11px;
			height: 20px;
			padding: 0 7px 0 6px;

			.v-chip__content {
				max-width: 100px;
				text-overflow: ellipsis;
				white-space: nowrap;
				overflow: hidden;
			}
		}

		::v-deep .v-input__pre-inner,
		::v-deep .v-input__append-inner {
			padding-top: 0 !important;
			padding-bottom: 0 !important;
		}
	}
}
</style>
